const ACTIVE_CLASS = 'header__menu-item--active'
const DEFAULT_TOGGLE_DELAY = 0
// Animation duration for navigation related animations
const DEFAULT_DURATION = 300

// Handle open on hover effects for nav megamenu toggles (click functionality is handled natively by the Popover API)
class NavigationPopoverToggle extends HTMLElement {
  static register(tagName) {
    customElements.define(
      tagName || 'navigation-popover-toggle',
      NavigationPopoverToggle
    )
  }

  toggleDelayTimeout = null

  toggleDuration(on = true) {
    const duration = on ? `0ms` : `${DEFAULT_DURATION}ms`
    document.body.style.setProperty('--duration-navigation', duration)
  }

  connectedCallback() {
    // Elements
    this.link = this.querySelector('a')
    this.relatedPopover = document.getElementById(
      this.querySelector('[popovertarget]')?.getAttribute('popovertarget')
    )

    if (!this.link || !this.relatedPopover) {
      return
    }
    this.bindEvents()
  }

  clearToggleDelayTimeout() {
    if (this.toggleDelayTimeout) {
      clearTimeout(this.toggleDelayTimeout)
    }
  }

  elementIsPopoverTrigger(el) {
    if (!el) {
      return false
    }
    return el.matches('[popovertarget]') || el.closest('[popovertarget]')
  }

  elementIsInDialog(el) {
    if (!el) {
      return false
    }
    return el.matches('dialog') || el.closest('dialog')
  }

  bindEvents() {
    this.link.addEventListener('mouseenter', (event) => {
      const { relatedTarget } = event
      this.clearToggleDelayTimeout()

      if (this.elementIsPopoverTrigger(relatedTarget)) {
        this.openMenu()
      } else {
        this.toggleDelayTimeout = setTimeout(() => {
          this.openMenu()
        }, DEFAULT_TOGGLE_DELAY)
      }
    })

    this.link.addEventListener('mouseleave', (event) => {
      const { relatedTarget } = event

      this.clearToggleDelayTimeout()

      if (!relatedTarget || relatedTarget.matches('html')) {
        return
      }

      // If mouse is entering a different popover trigger, zero out duration to prevent animation from showing when next one opens
      this.toggleDuration(this.elementIsPopoverTrigger(relatedTarget))

      // Close if mouse is not in the nav or the dialog (e.g. user hovered link and then moused to the right side of the header)
      if (!this.elementIsInDialog(relatedTarget)) {
        this.closeMenu()
      }
    })
  }

  openMenu() {
    this.relatedPopover.showPopover()
  }

  closeMenu() {
    if (this.relatedPopover.matches(':popover-open')) {
      this.relatedPopover.hidePopover()
    }
  }
}

NavigationPopoverToggle.register()

class NavigationPopoverControl extends HTMLElement {
  static register(tagName) {
    customElements.define(
      tagName || 'navigation-popover-control',
      NavigationPopoverControl
    )
  }

  connectedCallback() {
    // Elements
    this.relatedPopover = this.querySelector('[popover]')
    this.header = document.querySelector('sticky-header')
    this.headerLogoMenuContainer = document.querySelector(
      '.header-logo-menu-container'
    )
    this.relatedPopoverAnimation = null
    this.headerFillAnimation = null
    this.boundHandlePointerUp = this.handelPointerUp.bind(this)

    if (!this.relatedPopover) {
      return
    }

    this.bindEvents()
  }

  bindEvents() {
    this.relatedPopover.addEventListener('toggle', this.handleToggle.bind(this))

    this.relatedPopover.addEventListener(
      'mouseleave',
      this.handleMouseLeave.bind(this)
    )
  }

  elementIsInDialog(el) {
    if (!el) {
      return false
    }
    return el.matches('dialog') || el.closest('dialog')
  }

  // TLDR: Recovers clicks on the header that have been swallowed on iOS devices.
  // Details: The popover is positioned below the header so that the header can be clicked on mobile even if the menu is open.
  // This generally works except on iOS devices where the click never happens (probably due to popover, top-layer, etc. blocking it)
  // This function listens for pointerup events to find the click target, and then firest a click on it if needed
  handelPointerUp(event) {
    const { target } = event

    if (this.elementIsInDialog(target)) {
      return
    }
    const clickTarget = target.matches('a, button')
      ? target
      : target.closest('a, button')

    if (!clickTarget) {
      return
    }

    let hasBeenClicked = false

    clickTarget.addEventListener(
      'click',
      () => {
        hasBeenClicked = true
      },
      { once: true }
    )

    requestAnimationFrame(() => {
      if (hasBeenClicked) {
        return
      } else {
        clickTarget.click()
      }
    })
  }

  handleToggle(event) {
    const targetId = event.target.id
    const toggleElement = document.querySelector(
      `[popovertarget="${targetId}"]`
    )
    if (event.newState === 'open') {
      window.addEventListener('pointerup', this.boundHandlePointerUp)

      // Use to set min width of the megamenu
      if (this.headerLogoMenuContainer) {
        const { right } = this.headerLogoMenuContainer.getBoundingClientRect()
        document.body.style.setProperty('--menu-end', `${right || 0}px`)
      }
      toggleElement.classList.add(ACTIVE_CLASS)
      document.body.classList.add('menu-popover-open')
    } else {
      window.removeEventListener('pointerup', this.boundHandlePointerUp)
      toggleElement.classList.remove(ACTIVE_CLASS)

      // Don't remove the popover open class if parent popover is open (in the case of the mobile nav drawer)
      if (!document.querySelector('.mobile-nav-drawer-popover:popover-open')) {
        document.body.classList.remove('menu-popover-open')
      }
    }
  }

  handleMouseLeave(event) {
    const { relatedTarget } = event
    // Don't close if mouse is over the popver trigger of the open popover
    if (
      relatedTarget?.matches(`[popovertarget="${this.relatedPopover.id}"]`) ||
      relatedTarget?.closest(`[popovertarget="${this.relatedPopover.id}"]`) ||
      window.matchMedia('(max-width: 989px)').matches
    ) {
      return
    }
    // Comment out this line to prevent popover from closing when mouse leaves for UI debugging
    this.closePopover()
  }

  closePopover() {
    if (this.relatedPopover.matches(':popover-open')) {
      this.relatedPopover.hidePopover()
    }
  }
}

NavigationPopoverControl.register()

class MobileNavDrawer extends HTMLElement {
  static register(tagName) {
    customElements.define(tagName || 'mobile-nav-drawer', MobileNavDrawer)
  }

  connectedCallback() {
    const scripts = document.querySelectorAll(
      'script[data-for-mobile-nav-drawer]'
    )
    const items = []
    for (const script of scripts) {
      let data
      try {
        data = JSON.parse(script.textContent.trim())
      } catch {
        continue
      }
      if (data && typeof data.label === 'string') {
        items.push(data)
      }
    }

    const nav = document.createElement('nav')
    nav.className = 'mobile-nav-drawer__nav'
    nav.setAttribute('aria-label', 'Primary')

    const list = document.createElement('ul')
    list.className = 'mobile-nav-drawer__list'

    const listItemClass = 'mobile-nav-drawer__list-item'
    const itemClass = `mobile-nav-drawer__item focus-inset xir-h4 hover-underline`

    for (const item of items) {
      const li = document.createElement('li')
      li.className = listItemClass + (item.isFeaturedLink ? ' featured' : '')
      if (item.isFeaturedLink) {
        li.setAttribute('style', 'color: var(--featured-link-color);')
      }

      if (!item.hasSubmenus) {
        const link = document.createElement('a')
        const span = document.createElement('span')
        link.href = item.url
        span.textContent = item.label
        link.appendChild(span)
        link.className = itemClass
        li.appendChild(link)
      } else {
        const button = document.createElement('button')
        const span = document.createElement('span')
        button.type = 'button'
        span.textContent = item.label
        button.appendChild(span)
        button.className = itemClass
        if (item.popoverTarget) {
          button.setAttribute('popovertarget', item.popoverTarget)
        }
        li.appendChild(button)
        button.insertAdjacentHTML(
          'beforeend',
          `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8' fill='none' aria-hidden='true'>
  <path d="M4 0V8" stroke="currentColor"/>
  <path d="M0 4L8 4" stroke="currentColor"/>
</svg>`
        )
      }

      list.appendChild(li)
    }

    nav.appendChild(list)
    this.replaceChildren(nav)
  }
}

MobileNavDrawer.register()
